import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersApi, type AdminUserRow } from "../../api/users.api";
import { appsApi } from "../../api/apps.api";
import { paycomApi, type PaycomPreviewEmployee, type PaycomSyncResult } from "../../api/paycom.api";
import { useAuthStore } from "../../store/auth.store";

function sortByLastLogin(rows: AdminUserRow[], dir: "asc" | "desc") {
  const sign = dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    // Users who have never logged in sort to the bottom regardless of direction.
    if (!a.lastLoginAt && !b.lastLoginAt) return 0;
    if (!a.lastLoginAt) return 1;
    if (!b.lastLoginAt) return -1;
    return sign * (new Date(a.lastLoginAt).getTime() - new Date(b.lastLoginAt).getTime());
  });
}

export function AdminUsers() {
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showPaycomSync, setShowPaycomSync] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  const { data: users, isLoading } = useQuery({
    queryKey: ["users", { q }],
    queryFn: () => usersApi.list({ q: q || undefined }),
  });

  const toggleUser = useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      usersApi.update(userId, { disabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const bulkUpdate = useMutation({
    mutationFn: ({ disabled }: { disabled: boolean }) =>
      usersApi.bulkUpdate(Array.from(selected), disabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setSelected(new Set());
    },
  });

  const filteredUsers = sortByLastLogin(
    users?.filter((u) => !activeOnly || !u.disabled) ?? [],
    sortDir,
  );
  const allIds = filteredUsers.map((u) => u.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  function toggleSelectAll() {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  }

  function toggleSelectOne(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  return (
    <>
      <div className="page-header">
        <h2>Users</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowPaycomSync((v) => !v)}>
            {showPaycomSync ? "Cancel" : "Sync from Paycom"}
          </button>
          <button className="primary" onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Cancel" : "Add user"}
          </button>
        </div>
      </div>

      {showPaycomSync && (
        <PaycomSyncPanel
          onDone={() => {
            setShowPaycomSync(false);
            qc.invalidateQueries({ queryKey: ["users"] });
          }}
        />
      )}

      {showAdd && <AddUserForm onDone={() => setShowAdd(false)} />}

      <div style={{ marginBottom: 8 }}>
        <input
          type="search"
          placeholder="Search by name or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setSelected(new Set());
            }}
          />
          Active users only
        </label>
      </div>

      {someSelected && (
        <div
          className="card"
          style={{
            marginBottom: 12,
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "var(--bg-secondary, #f5f5f5)",
          }}
        >
          <span className="muted" style={{ fontSize: 14 }}>
            {selected.size} user{selected.size !== 1 ? "s" : ""} selected
          </span>
          <button
            className="secondary"
            style={{ padding: "4px 12px", fontSize: 13 }}
            disabled={bulkUpdate.isPending}
            onClick={() => bulkUpdate.mutate({ disabled: false })}
          >
            Enable selected
          </button>
          <button
            className="secondary"
            style={{ padding: "4px 12px", fontSize: 13 }}
            disabled={bulkUpdate.isPending}
            onClick={() => bulkUpdate.mutate({ disabled: true })}
          >
            Disable selected
          </button>
          <button
            style={{ padding: "4px 10px", fontSize: 13, marginLeft: "auto" }}
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
          {bulkUpdate.isError && (
            <span className="error-text" style={{ fontSize: 13 }}>
              Bulk update failed
            </span>
          )}
        </div>
      )}

      {isLoading && <p className="muted">Loading…</p>}

      {users && (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    style={{ width: "auto" }}
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Email</th>
                <th>Name</th>
                <th>Title / Dept</th>
                <th>Enabled</th>
                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  title="Sort by last login"
                >
                  Last login {sortDir === "desc" ? "▼" : "▲"}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} style={selected.has(u.id) ? { background: "var(--bg-selected, #eef4ff)" } : undefined}>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        style={{ width: "auto" }}
                        checked={selected.has(u.id)}
                        onChange={() => toggleSelectOne(u.id)}
                      />
                    </td>
                    <td>
                      <Link to={`/admin/users/${u.id}`}>{u.email}</Link>
                      {u.isPanelAdmin && (
                        <span className="badge success" style={{ marginLeft: 8 }}>admin</span>
                      )}
                    </td>
                    <td>{u.displayName ?? <span className="muted">—</span>}</td>
                    <td className="muted" style={{ fontSize: 13 }}>
                      {u.jobTitle || u.department ? (
                        <>
                          {u.jobTitle && <div>{u.jobTitle}</div>}
                          {u.department && <div style={{ fontSize: 12 }}>{u.department}</div>}
                        </>
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                    <td>
                      <label
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          cursor: isSelf ? "not-allowed" : "pointer",
                          opacity: isSelf ? 0.5 : 1,
                        }}
                        title={isSelf ? "You cannot disable your own account" : undefined}
                      >
                        <input
                          type="checkbox"
                          style={{ width: "auto" }}
                          checked={!u.disabled}
                          disabled={isSelf || toggleUser.isPending}
                          onChange={(e) =>
                            toggleUser.mutate({ userId: u.id, disabled: !e.target.checked })
                          }
                        />
                        <span style={{ fontSize: 13 }}>
                          {u.disabled ? (
                            <span className="badge danger">Disabled</span>
                          ) : (
                            <span className="badge success">Active</span>
                          )}
                        </span>
                      </label>
                    </td>
                    <td className="muted">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : (
                        <span className="badge muted">Pending first login</span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Link to={`/admin/users/${u.id}`}>Manage →</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function PaycomSyncPanel({ onDone }: { onDone: () => void }) {
  const [preview, setPreview] = useState<PaycomPreviewEmployee[] | null>(null);
  const [syncResult, setSyncResult] = useState<PaycomSyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewQ = useMutation({
    mutationFn: paycomApi.preview,
    onSuccess: (data) => setPreview(data),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? "Failed to fetch preview";
      setError(msg);
    },
  });

  const syncQ = useMutation({
    mutationFn: paycomApi.sync,
    onSuccess: (data) => {
      setSyncResult(data);
      onDone();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? "Sync failed";
      setError(msg);
    },
  });

  const createCount = preview?.filter((e) => e.action === "create").length ?? 0;
  const updateCount = preview?.filter((e) => e.action === "update").length ?? 0;
  const skipCount = preview?.filter((e) => e.action === "skip").length ?? 0;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Sync from Paycom</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Imports active employees from the latest Paycom employee export — links mobile phone, job
        title, and department to existing users (matched by Employee ID or email), and creates
        accounts for any employees not yet in the dashboard. Employees with an{" "}
        <strong>@swfunk.com</strong> address link to their Microsoft SSO account; others are keyed
        by their personal email and sign in to PmFlow via phone. The 4-digit backup code is
        entered manually per user on their profile page.
      </p>

      {error && <p className="error-text">{error}</p>}

      {syncResult && (
        <div style={{ marginBottom: 12 }}>
          <span className="badge success" style={{ marginRight: 8 }}>
            {syncResult.created} created
          </span>
          <span className="badge muted" style={{ marginRight: 8 }}>
            {syncResult.updated} updated
          </span>
          <span className="badge muted" style={{ marginRight: 8 }}>
            {syncResult.skipped} skipped
          </span>
          {syncResult.errors.length > 0 && (
            <span className="badge danger">{syncResult.errors.length} errors</span>
          )}
        </div>
      )}

      {!preview && !syncResult && (
        <button
          className="primary"
          onClick={() => { setError(null); previewQ.mutate(); }}
          disabled={previewQ.isPending}
        >
          {previewQ.isPending ? "Loading preview…" : "Preview import"}
        </button>
      )}

      {preview && !syncResult && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <span className="badge success">{createCount} to create</span>
            <span className="badge muted">{updateCount} to update</span>
            <span className="badge muted">{skipCount} skipped</span>
          </div>

          <div className="table-scroll" style={{ maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Email</th>
                  <th>Title / Dept</th>
                  <th>Phone</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((emp) => (
                  <tr key={emp.employeeId}>
                    <td>{emp.displayName || <span className="muted">—</span>}</td>
                    <td className="muted" style={{ fontSize: 13 }}>{emp.email ?? "—"}</td>
                    <td className="muted" style={{ fontSize: 13 }}>
                      {emp.jobTitle || emp.department ? (
                        <>
                          {emp.jobTitle && <div>{emp.jobTitle}</div>}
                          {emp.department && <div style={{ fontSize: 12 }}>{emp.department}</div>}
                        </>
                      ) : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {emp.hasMobile ? "✓" : <span className="muted">—</span>}
                    </td>
                    <td>
                      {emp.action === "create" && <span className="badge success">Create</span>}
                      {emp.action === "update" && <span className="badge muted">Update</span>}
                      {emp.action === "skip" && (
                        <span className="badge danger" title={emp.skipReason}>Skip</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="primary"
              onClick={() => { setError(null); syncQ.mutate(); }}
              disabled={syncQ.isPending || (createCount === 0 && updateCount === 0)}
            >
              {syncQ.isPending ? "Syncing…" : `Run sync (${createCount + updateCount} changes)`}
            </button>
            <button onClick={onDone}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

function AddUserForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isPanelAdmin, setIsPanelAdmin] = useState(false);
  const [selectedAppIds, setSelectedAppIds] = useState<Set<string> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const appsQ = useQuery({
    queryKey: ["apps"],
    queryFn: appsApi.list,
    select: (apps) => apps,
  });

  const apps = appsQ.data ?? [];
  const effectiveSelected = selectedAppIds ?? new Set(apps.filter((a) => a.isDefault).map((a) => a.id));

  function toggleApp(appId: string) {
    const next = new Set(effectiveSelected);
    if (next.has(appId)) next.delete(appId);
    else next.add(appId);
    setSelectedAppIds(next);
  }

  const add = useMutation({
    mutationFn: () =>
      usersApi.create({
        email,
        displayName: displayName || undefined,
        isPanelAdmin,
        appIds: Array.from(effectiveSelected),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      onDone();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? "Failed to add user";
      setError(msg);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    add.mutate();
  }

  return (
    <form className="card form-grid" onSubmit={onSubmit} style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Add user</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Creates a placeholder account. The user's Microsoft Entra identity will be linked
        automatically when they first sign in.
      </p>
      <div>
        <label htmlFor="add-email">Microsoft 365 email</label>
        <input
          id="add-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@swfunk.com"
          pattern="^[^@]+@swfunk\.com$"
          title="Must be a @swfunk.com email address"
          required
        />
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
          Must be a @swfunk.com Microsoft work address
        </p>
      </div>
      <div>
        <label htmlFor="add-name">Display name (optional)</label>
        <input
          id="add-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          style={{ width: "auto" }}
          checked={isPanelAdmin}
          onChange={(e) => setIsPanelAdmin(e.target.checked)}
        />
        Grant panel admin access
      </label>

      {apps.length > 0 && (
        <div>
          <label style={{ marginBottom: 8, display: "block" }}>App access</label>
          <div style={{ display: "grid", gap: 6 }}>
            {apps.map((app) => (
              <label key={app.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={effectiveSelected.has(app.id)}
                  onChange={() => toggleApp(app.id)}
                />
                <span>{app.name}</span>
                <span className="badge muted">{app.environment}</span>
                {app.isDefault && <span className="badge muted">default</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      <button type="submit" className="primary" disabled={add.isPending}>
        {add.isPending ? "Adding…" : "Add user"}
      </button>
      {error && <div className="error-text">{error}</div>}
    </form>
  );
}
